// src/components/SensorChart.js
import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Brush, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const SensorChart = ({ 
  sensor, 
  chartData, 
  sensorType, 
  displayedMetrics,
  dataAggregation,
  formatItalianTime
}) => {
  // Verifica se ci sono dati da visualizzare
  if (!chartData || !chartData.length) {
    return (
      <div className="alert alert-info">
        Nessun dato disponibile per il grafico
      </div>
    );
  }
  
  // Trova valori min e max per Y axis
  const getChartDomain = (dataKey) => {
    const values = chartData
      .map(d => parseFloat(d[dataKey]))
      .filter(v => !isNaN(v) && v !== null);
      
    if (values.length === 0) {
      // Default domains se non ci sono dati
      const defaults = {
        temperatura: [0, 40],
        umidita: [0, 100],
        livello: [0, 100],
        lightIntensity: [0, 1000],
        uvIndex: [0, 10],
        windSpeed: [0, 100],
        pressure: [900, 1100],
        dewPoint: [0, 40]
      };
      
      return defaults[dataKey] || [0, 100];
    }
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    
    // Aggiungi margine
    return [Math.floor(min - (max-min)*0.1), Math.ceil(max + (max-min)*0.1)];
  };
  
  // Gestione rendering del grafico in base al tipo di sensore
  if (sensorType === 'level') {
    return (
      <div className="mt-3">
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Livello</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('livello')}
                  tick={{ fontSize: 10 }}
                  width={35}
                  tickFormatter={value => value.toFixed(1)}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)} cm`, 'Livello']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="livello" 
                  stroke="#0d6efd" 
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#0d6efd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mt-3">
      {/* GRAFICO TEMPERATURA */}
      {displayedMetrics.includes('temperatura') && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Temperatura</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('temperatura')}
                  tick={{ fontSize: 10 }}
                  width={35}
                  tickFormatter={value => value.toFixed(1)}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)}°C`, 'Temperatura']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="temperatura" 
                  stroke="#dc3545" 
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#dc3545"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* GRAFICO UMIDITÀ */}
      {displayedMetrics.includes('umidita') && sensorType !== 'level' && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Umidità</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={[0, 100]}
                  tick={{ fontSize: 10 }}
                  width={35}
                  tickFormatter={value => value.toFixed(0)}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)}%`, 'Umidità']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="umidita" 
                  stroke="#0d6efd" 
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#0d6efd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* GRAFICO INTENSITÀ LUMINOSA (solo per stazione meteo) */}
      {sensorType === 'weather' && displayedMetrics.includes('lightIntensity') && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Intensità Luminosa</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('lightIntensity')}
                  tick={{ fontSize: 10 }}
                  width={40}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(0)} lux`, 'Intensità Luminosa']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="lightIntensity" 
                  stroke="#fd7e14" // Arancione per la luce
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#fd7e14"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* GRAFICO VELOCITÀ DEL VENTO (solo per stazione meteo) */}
      {sensorType === 'weather' && displayedMetrics.includes('windSpeed') && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Velocità Vento</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('windSpeed')}
                  tick={{ fontSize: 10 }}
                  width={40}
                  tickFormatter={value => value.toFixed(1)}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)} km/h`, 'Velocità Vento']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="windSpeed" 
                  stroke="#20c997" // Verde acqua per il vento
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#20c997"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* GRAFICO PRESSIONE (solo per stazione meteo) */}
      {sensorType === 'weather' && displayedMetrics.includes('pressure') && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Pressione</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('pressure')}
                  tick={{ fontSize: 10 }}
                  width={40}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)} hPa`, 'Pressione']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="pressure" 
                  stroke="#6c757d" 
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#6c757d"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* GRAFICO PUNTO DI RUGIADA */}
      {displayedMetrics.includes('dewPoint') && chartData.some(d => !isNaN(parseFloat(d.dewPoint))) && (
        <div className="mb-4">
          <h4 className="fw-semibold mb-2 text-muted small">Andamento Punto di Rugiada</h4>
          <div style={{height: '180px'}}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateString" 
                  tick={{ fontSize: 10 }} 
                  interval="preserveStartEnd"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, dataAggregation === 'daily' ? 'dd/MM' : 'HH:mm', { locale: it });
                  }}
                />
                <YAxis 
                  domain={getChartDomain('dewPoint')}
                  tick={{ fontSize: 10 }}
                  width={40}
                  tickFormatter={value => value.toFixed(1)}
                />
                <Tooltip 
                  labelFormatter={(value) => `Data: ${formatItalianTime(value, 'dd/MM/yyyy HH:mm')}`}
                  formatter={(value) => [`${parseFloat(value).toFixed(1)}°C`, 'Punto di Rugiada']}
                  contentStyle={{ fontSize: '12px' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="dewPoint" 
                  stroke="#6610f2" 
                  dot={false} 
                  strokeWidth={2}
                  isAnimationActive={false}
                  connectNulls={true}
                />
                <Brush 
                  dataKey="dateString" 
                  height={20} 
                  stroke="#6610f2"
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return format(date, 'dd/MM', { locale: it });
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default SensorChart;